import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectAllPoints, selectSortedTsps } from '../map/slice/mapSelectors';
import { useGetTSPsQuery, useGetZonesQuery } from '../services/edgeApi';

const POINT_TYPES = [
  { value: 'INT', label: 'INT' },
  { value: 'PHANTOM', label: 'PHANTOM' },
  { value: 'LDC', label: 'LDC' },
  { value: 'WHD', label: 'WHD' },
  { value: 'END', label: 'END' },
  { value: 'OTH', label: 'OTH' },
  { value: 'GTH', label: 'GTH' },
  { value: 'STR', label: 'STR' },
  { value: 'PPT', label: 'PPT' },
  { value: 'LNG', label: 'LNG' },
  { value: 'VIR', label: 'VIR' },
  { value: 'COM', label: 'COM' },
  { value: 'PLT', label: 'PLT' },
  { value: 'EGN', label: 'EGN' },
  { value: 'RNG', label: 'RNG' },
  { value: 'STA', label: 'STA' },
];

/**
 * @typedef {{
 *  pointId: import('../services/edgeApi').Point['id'] | null;
 *  loc_name: import('../services/edgeApi').Point['loc_name'];
 *  loc_id: import('../services/edgeApi').Point['loc_id'];
 *  tsp_id: import('../services/edgeApi').Point['tsp_id'];
 *  type: import('../services/edgeApi').Point['type'];
 *  dir_flo: import('../services/edgeApi').Point['dir_flo'];
 *  receipt_zone: import('../services/edgeApi').Point['receipt_zone'];
 *  delivery_zone: import('../services/edgeApi').Point['delivery_zone'];
 *  needs_review: import('../services/edgeApi').Point['needs_review'];
 *  review_notes: import('../services/edgeApi').Point['review_notes'];
 * }} PointFormData
 */

/**
 *
 * @typedef {{
 *  pointId?: string | number;
 *  onSave: (pointId: string | number, payload: PointFormData) => void;
 *  onCancel: () => void;
 * }} PointFormProps
 */

/** @type {React.FC<PointFormProps>} */
export const PointForm = ({ pointId, onSave, onCancel }) => {
  /** @type {import('../types/types').State<PointFormData>} */
  const [formData, setFormData] = React.useState({
    loc_name: '',
    loc_id: '',
    tsp_id: '',
    type: '',
    dir_flo: '',
    receipt_zone: '',
    delivery_zone: '',
    needs_review: false,
    review_notes: '',
  });

  const editMode = pointId !== null && pointId !== undefined;

  useGetTSPsQuery();
  const { data: zones } = useGetZonesQuery({ tsp: formData.tsp });

  const points = useSelector(selectAllPoints);
  const tsps = useSelector(selectSortedTsps);

  const point = useMemo(
    () => (pointId ? points[pointId] : null),
    [points, pointId],
  );

  useEffect(() => {
    if (point) {
      setFormData({
        pointId: point.id,
        loc_name: point.loc_name,
        loc_id: point.loc_id,
        tsp_id: point.tsp_id,
        type: point.loc_type_ind,
        dir_flo: point.dir_flo,
        receipt_zone: point.receipt_zone,
        delivery_zone: point.delivery_zone,
        needs_review: Boolean(point.needs_review),
        review_notes: point.review_notes,
      });
    } else {
      setFormData({
        loc_name: '',
        loc_id: '',
        tsp_id: '',
        type: '',
        dir_flo: '',
        receipt_zone: '',
        delivery_zone: '',
        needs_review: false,
        review_notes: '',
      });
    }
  }, [point]);

  /**
   *
   * @param {React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>} e
   */
  const handleChange = (e) => {
    const { name } = e.target;
    const isCheckbox = e.target.type === 'checkbox';
    const isChecked = e.target.checked;
    const value = isCheckbox ? isChecked : e.target.value;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = () => {
    if (point) {
      onSave(point.id, formData);
    } else {
      onSave(null, formData);
    }
  };

  return (
    <div>
      <small className="text-muted">Required fields *</small>
      <hr />
      <form className="container d-grid gap-4">
        <div className="row">
          <div className="col-xs-12 col-sm-8">
            <label className="form-label" htmlFor="loc_name">
              Location Name *
            </label>
            <input
              type="text"
              className="form-control"
              name="loc_name"
              placeholder="Location Name"
              value={formData.loc_name}
              onChange={handleChange}
            />
          </div>
          <div className="col-xs-12 col-sm-4">
            <label className="form-label" htmlFor="loc_id">
              Location ID *
            </label>
            <input
              type="text"
              className="form-control"
              name="loc_id"
              placeholder="Location Name"
              value={formData.loc_id}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-4">
            <label className="form-label" htmlFor="tsp_id">
              TSP *
            </label>
            <select
              className="form-select"
              name="tsp_id"
              value={formData.tsp_id}
              onChange={handleChange}
              disabled={editMode}
            >
              <option value="" disabled>
                Select TSP
              </option>
              {tsps.map((tsp) => (
                <option key={tsp.id.toString()} value={tsp.id}>
                  {tsp.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-4">
            <label className="form-label" htmlFor="type">
              Type
            </label>
            <select
              className="form-select"
              name="type"
              value={formData.type}
              onChange={handleChange}
            >
              <option disabled value="">
                Select Type
              </option>
              {POINT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-4">
            <label className="form-label" htmlFor="dir_flo">
              Direction
            </label>
            <select
              className="form-select"
              name="dir_flo"
              value={formData.dir_flo}
              onChange={handleChange}
            >
              <option value="" disabled>
                Select Direction
              </option>
              <option value="D">Delivery</option>
              <option value="R">Receipt</option>
              <option value="B">Bidirectional</option>
            </select>
          </div>
        </div>

        <div className="row">
          <div className="col-6">
            <label className="form-label" htmlFor="receipt_zone">
              Receipt Zone
            </label>
            <select
              className="form-select"
              name="receipt_zone"
              value={formData.receipt_zone}
              onChange={handleChange}
            >
              <option value="" disabled>
                Select Zone
              </option>
              {Object.values(zones ?? {}).map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {zone.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-6">
            <label className="form-label" htmlFor="delivery_zone">
              Delivery Zone
            </label>
            <select
              className="form-select"
              name="delivery_zone"
              value={formData.delivery_zone}
              onChange={handleChange}
            >
              <option value="" disabled>
                Select Zone
              </option>
              {Object.values(zones ?? {}).map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {zone.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="row">
          <div className="col-12 form-check form-switch">
            <label className="form-check-label" htmlFor="needs_review">
              Needs Review
            </label>
            <input
              type="checkbox"
              className="form-check-input"
              name="needs_review"
              checked={formData.needs_review}
              onChange={handleChange}
            />
          </div>
        </div>

        {formData.needs_review && (
          <div className="row">
            <div className="col-12">
              <label className="form-label" htmlFor="review_notes">
                Review Notes
              </label>
              <textarea
                className="form-control"
                name="review_notes"
                rows={3}
                placeholder="Review Notes"
                value={formData.review_notes}
                onChange={handleChange}
              />
            </div>
          </div>
        )}
      </form>
      <hr />
      <div className="float-end">
        <button type="button" className="btn btn-primary" onClick={handleSave}>
          Save
        </button>
        <button
          type="button"
          className="btn btn-secondary mx-2"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};
